import React, { useState, useMemo } from "react";
import {
  Box,
  Stack,
  useMediaQuery,
  useTheme,
  Button,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { Receipt } from "@mui/icons-material";

import PageHeader from "../../components/page-layout/PageHeader";
import MobileHeader from "../../components/layout/MobileHeader";
import TransactionsSummaryCards from "../../components/transactions/TransactionsSummaryCards";
import TransactionsWeeklyList from "../../components/transactions/TransactionsWeeklyList";
import TransactionsFilters from "../../components/transactions/TransactionsFilters";
import { mockTransactionsData } from "../../mocks/transactionsMockData";

const Transactions = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate();

  const [filters, setFilters] = useState({
    search: "",
    year: "",
    month: "",
    status: "",
  });

  // Helper function to check if search matches date in week
  const searchMatchesWeek = (searchText, week) => {
    if (!searchText.trim()) return true;

    const searchLower = searchText.toLowerCase().trim();

    // Check week label
    if (week.weekLabel.toLowerCase().includes(searchLower)) {
      return true;
    }

    // Parse search for date components
    const monthNames = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];
    const monthNamesLong = ['january', 'february', 'march', 'april', 'may', 'june', 'july', 'august', 'september', 'october', 'november', 'december'];
    
    // Extract month and day from search
    let searchMonth = null;
    let searchDay = null;
    let searchYear = null;

    // Check for month name in search
    monthNames.forEach((month, index) => {
      if (searchLower.includes(month)) {
        searchMonth = index;
      }
    });
    monthNamesLong.forEach((month, index) => {
      if (searchLower.includes(month)) {
        searchMonth = index;
      }
    });

    // Extract numbers from search
    const numbers = searchText.match(/\d+/g);
    if (numbers) {
      // First number could be day or month
      if (numbers[0]) {
        const firstNum = parseInt(numbers[0]);
        if (firstNum >= 1 && firstNum <= 31) {
          searchDay = firstNum;
        }
      }
      // Second number could be year
      if (numbers[1]) {
        const secondNum = parseInt(numbers[1]);
        if (secondNum >= 2000 && secondNum <= 2100) {
          searchYear = secondNum;
        } else if (secondNum >= 1 && secondNum <= 31 && !searchDay) {
          searchDay = secondNum;
        }
      }
    }

    // If we found month and/or day, check against week range
    if (searchMonth !== null || searchDay !== null) {
      const startDate = new Date(week.startDate);
      const endDate = new Date(week.endDate);

      // Generate all dates in the week range
      const currentDate = new Date(startDate);
      while (currentDate <= endDate) {
        const dateMonth = currentDate.getMonth();
        const dateDay = currentDate.getDate();
        const dateYear = currentDate.getFullYear();

        let matches = true;

        if (searchMonth !== null && dateMonth !== searchMonth) {
          matches = false;
        }
        if (searchDay !== null && dateDay !== searchDay) {
          matches = false;
        }
        if (searchYear !== null && dateYear !== searchYear) {
          matches = false;
        }

        if (matches) {
          return true;
        }

        currentDate.setDate(currentDate.getDate() + 1);
      }
    }

    // Check if search matches any date in dailySummaries
    if (week.dailySummaries) {
      for (const day of week.dailySummaries) {
        if (day.date.toLowerCase().includes(searchLower)) {
          return true;
        }
      }
    }

    return false;
  };

  // Filter weeks
  const filteredWeeks = useMemo(() => {
    return mockTransactionsData.weeks.filter((week) => {
      // Search filter
      if (!searchMatchesWeek(filters.search, week)) {
        return false;
      }

      // Year filter
      if (filters.year) {
        const weekYear = new Date(week.startDate).getFullYear().toString();
        if (weekYear !== filters.year) return false;
      }

      // Month filter
      if (filters.month) {
        const weekMonth = (new Date(week.startDate).getMonth() + 1).toString().padStart(2, "0");
        if (weekMonth !== filters.month) return false;
      }

      // Status filter
      if (filters.status && filters.status !== "") {
        if (week.status !== filters.status) {
          return false;
        }
      }

      return true;
    });
  }, [filters]);

  // Extract available years and months from weeks
  const availableYears = useMemo(() => {
    const years = mockTransactionsData.weeks.map((week) =>
      new Date(week.startDate).getFullYear().toString()
    );
    return [...new Set(years)].sort((a, b) => b - a);
  }, []);

  const availableMonths = useMemo(() => {
    const months = mockTransactionsData.weeks.map((week) =>
      (new Date(week.startDate).getMonth() + 1).toString().padStart(2, "0")
    );
    return [...new Set(months)].sort();
  }, []);

  const handleGetInvoice = () => {
    // Navigate to AllTransactions page with no specific date (all transactions)
    navigate("/all-transactions", {
      state: { date: null },
    });
  };

  return (
    <Stack sx={{ px: isMobile ? 1 : 0 }}>
      {/* Header */}
      {isMobile ? (
        <Box>
          <MobileHeader title="Transactions" />
          <Box sx={{ px: 1, pt: 1 }}>
            <Button
              variant="outlined"
              size="small"
              fullWidth
              startIcon={<Receipt />}
              onClick={handleGetInvoice}
            >
              View All Invoices
            </Button>
          </Box>
        </Box>
      ) : (
        <PageHeader
          title="Transactions"
          lead="View and manage your weekly transaction summaries"
          rightComponent={
            <Button
              variant="outlined"
              size="small"
              startIcon={<Receipt />}
              onClick={handleGetInvoice}
            >
              View All Invoices
            </Button>
          }
        />
      )}

      {/* Main Content */}
      <Box
        sx={{
          px: isMobile ? 0 : 2,
          display: "flex",
          flexDirection: "column",
          gap: 2,
        }}
      >
        {/* Summary Cards */}
        <TransactionsSummaryCards
          data={mockTransactionsData.summary}
          loading={false}
        />

        {/* Filters */}
        <TransactionsFilters
          filters={filters}
          onFiltersChange={setFilters}
          isMobile={isMobile}
          availableYears={availableYears}
          availableMonths={availableMonths}
        />

        {/* Weekly Transactions List */}
        <TransactionsWeeklyList weeks={filteredWeeks} loading={false} />
      </Box>
    </Stack>
  );
};

export default Transactions;
